import { live } from '@electric-sql/pglite/live';
import { PGliteWorker } from '@electric-sql/pglite/worker';
import { type Migration, migrate } from '@quickreport/db';

const files = import.meta.glob('../../../../packages/db/drizzle/*.sql', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const MIGRATIONS: Migration[] = Object.entries(files).map(([path, sql]) => ({
  tag:
    path
      .split('/')
      .pop()
      ?.replace(/\.sql$/, '') ?? path,
  sql,
}));

export type LocalDb = PGliteWorker & { live: import('@electric-sql/pglite/live').LiveNamespace };

/** The browser database's name. The number changes only when the storage layout breaks. */
export const DB_NAME = 'quickreport2';

let ready: Promise<LocalDb> | undefined;
let worker: Worker | undefined;

/** Open the local database in a worker and bring its tables up to date. */
export function openLocalDb(): Promise<LocalDb> {
  ready ??= (async () => {
    const t0 = performance.now();
    worker = new Worker(new URL('./pglite.worker.ts', import.meta.url), { type: 'module' });
    const db = (await PGliteWorker.create(worker, { extensions: { live } })) as LocalDb;
    await migrate({ exec: (s) => db.exec(s), query: (s, p) => db.query(s, p) }, MIGRATIONS);
    console.info(`[local-db] ready in ${(performance.now() - t0).toFixed(0)} ms`);
    return db;
  })();
  return ready;
}

/**
 * Delete everything stored on this device: used on sign-out and when a device is removed
 * from the lab, so the next person on a shared computer sees none of it.
 */
export async function wipeLocalDb(): Promise<void> {
  const db = ready ? await ready.catch(() => undefined) : undefined;
  await db?.close().catch(() => undefined);
  worker?.terminate();
  ready = undefined;
  worker = undefined;
  await Promise.all(
    [`/pglite/${DB_NAME}`, DB_NAME].map(
      (name) =>
        new Promise<void>((resolve) => {
          const req = indexedDB.deleteDatabase(name);
          req.onsuccess = req.onerror = req.onblocked = () => resolve();
        }),
    ),
  );
}
